import {useEffect, useState} from 'react';
import {
  view,
  Action,
  Icon,
  useCollapsible,
  CollapsibleContainer,
  tokens,
} from '@sail/ui';
import {useAnalytics} from '@stripe-internal/inline-data';
import {defineMessages, useIntl} from 'react-intl';
import {StripeAssistantLiveAnswersSource} from '@stripe-internal/conversations-client';
import {SourceLink} from './SourcesLink';

const messages = defineMessages({
  viewSources: {
    id: 'conversations_ui.live_answers_response.sources_dropdown.view_sources',
    description: 'Label to expand live answers sources dropdown',
    defaultMessage:
      '{sourcesCount, plural, one {View # source} other {View # sources}}',
  },
  hideSources: {
    id: 'conversations_ui.live_answers_response.sources_dropdown.hide_sources',
    description: 'Label to collapse live answers sources dropdown',
    defaultMessage:
      '{sourcesCount, plural, one {Hide # source} other {Hide # sources}}',
  },
});

export const SourcesDropdown = ({
  sources,
}: {
  sources: Array<StripeAssistantLiveAnswersSource>;
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const [isOpen, setOpen] = useState(false);
  const [markedOpen, setMarkedOpen] = useState(false);
  const {triggerProps, collapsibleProps, icon} = useCollapsible({
    open: isOpen,
    iconOrientation: 'right-down',
  });

  useEffect(() => {
    if (isOpen) {
      analytics.action(
        'universal_help_live_answers_frontend_sources_opened',
        {},
      );
      setMarkedOpen(true);
    } else if (markedOpen) {
      analytics.action(
        'universal_help_live_answers_frontend_sources_closed',
        {},
      );
      setMarkedOpen(false);
    }
  }, [isOpen, analytics, markedOpen]);

  const rows = sources.map((source, i) => (
    <SourceLink
      source={source}
      // eslint-disable-next-line react/no-array-index-key
      key={`live-answers-source-${i}`}
    />
  ));

  return (
    <view.div>
      <Action
        onPress={() => {
          setOpen(!isOpen);
        }}
        {...triggerProps}
        css={{
          stack: 'x',
          alignX: 'start',
          alignY: 'center',
          gap: 'small',
          color: tokens.color.text.subdued.toString(),
          font: 'label.small.emphasized',

          ':hover': {
            color: 'text',
          },
        }}
      >
        <Icon icon={icon} size="xxsmall" css={{color: 'inherit'}} />
        {intl.formatMessage(
          isOpen ? messages.hideSources : messages.viewSources,
          {sourcesCount: sources.length},
        )}
      </Action>
      <CollapsibleContainer {...collapsibleProps}>
        <view.div
          css={{
            stack: 'y',
            // The new design has a default line height unlike the old design, so this needs to be adjusted
            gap: 'small',
            paddingY: 'medium',
          }}
        >
          {rows}
        </view.div>
      </CollapsibleContainer>
    </view.div>
  );
};
